import React, { useEffect, useState } from 'react';
import { AlertCircle, Copy, Download, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './UI';

interface TwoFactorSetupProps {
  // Bei erzwungenem Setup (Login-Flow) gesetzt; bei freiwilligem Setup (Account-Seite) leer,
  // dann wird das reguläre Auth-Token aus dem AuthContext verwendet.
  pendingToken?: string;
  onComplete: (recoveryCodes: string[]) => void;
  onCancel?: () => void;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ pendingToken, onComplete, onCancel }) => {
  const { t } = useTranslation();
  const { fetchTwoFactorSetup, completeTwoFactorSetup, applyLoginToken } = useAuth();

  const [loadingSetup, setLoadingSetup] = useState(true);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [sharedKey, setSharedKey] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [pendingTokenPayload, setPendingTokenPayload] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSetup(true);
    fetchTwoFactorSetup(pendingToken)
      .then((info) => {
        if (cancelled) return;
        setQrCodeImage(info.qrCodeImage);
        setSharedKey(info.sharedKey);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t('auth:twoFactor.setupFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoadingSetup(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingToken]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { recoveryCodes: codes, tokenPayload } = await completeTwoFactorSetup(code, pendingToken);
      setRecoveryCodes(codes);
      setPendingTokenPayload(tokenPayload);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth:twoFactor.invalidCode'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (recoveryCodes) {
      navigator.clipboard.writeText(recoveryCodes.join('\n')).catch(() => undefined);
    }
  };

  const downloadRecoveryCodes = () => {
    if (!recoveryCodes) return;
    const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'canebase-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (recoveryCodes) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-green-400" size={24} />
          <h3 className="text-lg font-bold text-dark-50">{t('auth:twoFactor.enabledTitle')}</h3>
        </div>
        <p className="text-sm text-dark-300">
          {t('auth:twoFactor.recoveryCodesHint')}
        </p>
        <div className="bg-dark-700 border border-dark-600 rounded-lg p-3 font-mono text-sm text-dark-100 grid grid-cols-2 gap-2">
          {recoveryCodes.map((rc) => (
            <span key={rc}>{rc}</span>
          ))}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" icon={<Copy size={14} />} onClick={copyRecoveryCodes}>
            {t('auth:twoFactor.copy')}
          </Button>
          <Button type="button" variant="ghost" size="sm" icon={<Download size={14} />} onClick={downloadRecoveryCodes}>
            {t('auth:twoFactor.download')}
          </Button>
        </div>
        <Button
          type="button"
          fullWidth
          onClick={() => {
            applyLoginToken(pendingTokenPayload);
            onComplete(recoveryCodes);
          }}
        >
          {t('auth:twoFactor.done')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-dark-50">{t('auth:twoFactor.setupTitle')}</h3>
        <p className="text-sm text-dark-400 mt-1">
          {t('auth:twoFactor.setupDescription')}
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex gap-3">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {loadingSetup ? (
        <div className="flex justify-center py-8">
          <span className="w-6 h-6 border-2 border-dark-500 border-t-primary-400 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {qrCodeImage && (
            <div className="flex justify-center bg-white rounded-lg p-3">
              <img src={qrCodeImage} alt={t('auth:twoFactor.qrAlt')} width={200} height={200} />
            </div>
          )}
          {sharedKey && (
            <p className="text-xs text-dark-400 text-center break-all">
              {t('auth:twoFactor.manualEntry')} <span className="font-mono text-dark-200">{sharedKey}</span>
            </p>
          )}

          <form onSubmit={handleConfirm} className="space-y-3">
            <div>
              <label className="label-base">{t('auth:twoFactor.authenticatorCodeLabel')}</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="input-base bg-dark-700 border-dark-600 text-dark-50 placeholder-dark-400 tracking-widest text-center"
                required
                disabled={submitting}
              />
            </div>
            <div className="flex gap-2">
              {onCancel && (
                <Button type="button" variant="ghost" fullWidth onClick={onCancel} disabled={submitting}>
                  {t('auth:twoFactor.cancel')}
                </Button>
              )}
              <Button type="submit" fullWidth loading={submitting}>
                {t('auth:twoFactor.confirmAndActivate')}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
